import React, { useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { IImage } from 'tbk-components/src/components/Image';
import Modal from 'tbk-components/src/components/Modal';
import { Generic } from 'react-structured-data';
import LazyJSONLD from '../LazyJSONLD';

export interface IVideo {
	src: string;
}

export interface IVideoProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	schema?: any;
	url?: IVideo;
	/**
	 * You can paste embed code from video streaming sites like YouTube or Vimeo here.
	 * @control textarea
	 */
	oembed?: string;
	poster?: IImage;
	playLabel?: string;
	autoPlay?: boolean;
	/**
	 * @noUI
	 */
	disableControls?: boolean;
	/**
	 * @noUI
	 */
	muted?: boolean;
	/**
	 * @noUI
	 */
	loop?: boolean;
	/**
	 * Forces video render even if oembed is given. This should be used with AdvancedMedia
	 * @noUI
	 */
	videoWallpaper?: boolean;
	/**
	 * To control modal from parent components
	 * @noUI
	 */
	viewModal?: boolean;
	/**
	 * @noUI
	 */
	hideVideoButton?: boolean;
}

export const getYoutubeIDFromURL = (url: string): string | boolean => {
	const regExp =
		/(https?:\/\/)?(((m|www)\.)?(youtube(-nocookie)?|youtube.googleapis)\.com.*(v\/|v=|vi=|vi\/|e\/|embed\/|user\/.*\/u\/\d+\/)|youtu\.be\/)([_0-9a-z-]+)/i;
	const match = url.match(regExp);
	return match && match[8].length === 11 ? match[8] : false;
};

/**
 * Video
 * @block
 * @requiresHooks
 */

const Video: React.FC<IVideoProps> = ({
	id,
	className,
	classNames = [],
	schema = [],
	url,
	oembed,
	poster,
	playLabel = 'Play video',
	autoPlay = false,
	disableControls = false,
	muted = false,
	loop = false,
	videoWallpaper = false,
	viewModal = false,
	hideVideoButton = false,
}) => {
	const [isPlaying, setIsPlaying] = useState(autoPlay);
	const [showModal, setShowModal] = useState(viewModal);
	const [thumbURL, setThumbURL] = useState(poster?.src || '');
	const [aspectRatio, setAspectRatio] = useState('16/9');
	const ref = useRef<HTMLVideoElement | null>(null);
	const videoModalRef = useRef<HTMLDivElement | null>(null);

	// Set up native video event listeners
	useEffect(() => {
		if (!ref.current || !url || oembed) return;

		const video = ref.current;

		const handlePlay = () => setIsPlaying(true);
		const handlePause = () => setIsPlaying(false);

		video.addEventListener('play', handlePlay);
		video.addEventListener('pause', handlePause);

		return () => {
			video.removeEventListener('play', handlePlay);
			video.removeEventListener('pause', handlePause);
		};
	}, [ref, url, oembed]);

	// Control native video playback on `isPlaying` change
	useEffect(() => {
		if (!ref.current) return;
		const video = ref.current;

		isPlaying ? video.play().catch(() => {}) : video.pause();
	}, [isPlaying]);

	// Extract and handle oEmbed content
	useEffect(() => {
		if (!oembed) return;

		const matches = oembed.match(/\bhttps?:\/\/\S+/gi);
		const embedURL = matches?.[0];

		if (!embedURL) return;

		if (embedURL.includes('youtube.com/') || embedURL.includes('youtu.be/')) {
			if (!thumbURL) {
				const ytID = getYoutubeIDFromURL(embedURL);
				if (ytID) {
					setThumbURL(`https://i1.ytimg.com/vi/${ytID}/maxresdefault.jpg`);
				}
			}
		} else if (embedURL.includes('vimeo.com/')) {
			if (!thumbURL) {
				const fetchURL = `https://vimeo.com/api/oembed.json?url=${embedURL}`;
				fetch(fetchURL)
					.then((res) => res.json())
					.then((data) => {
						setThumbURL(data.thumbnail_url);
						setAspectRatio(`${data.width}/${data.height}`);
					})
					.catch((err) => console.error('Vimeo oEmbed fetch error:', err));
			}
		}
	}, [oembed, thumbURL]);

	const addTabIndex = () => {
		setTimeout(() => {
			const iframe = videoModalRef.current?.querySelector('iframe');
			if (iframe && !iframe.hasAttribute('tabindex')) {
				iframe.setAttribute('tabindex', '0');
			}
		}, 0);
	};

	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'video',
					isPlaying && 'video--playing',
					thumbURL && 'video--poster',
					videoWallpaper && 'video--wallpaper',
					...classNames,
				)
			}
			style={
				!isPlaying && thumbURL
					? {
						backgroundImage: `url("${thumbURL}")`,
						aspectRatio
					}
					: {}
			}
		>
			{schema && (
				<LazyJSONLD>
					<Generic
						type="page"
						jsonldtype="VideoObject"
						schema={schema}
					/>
				</LazyJSONLD>
			)}

			{((videoWallpaper && url) ||
				(url?.src && !oembed) ||
				poster?.src) && (
				<video
					ref={ref}
					controls={!disableControls}
					poster={poster?.src}
					autoPlay={false}
					muted={muted}
					loop={loop}
					playsInline={loop}
				>
					<source src={url?.src} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			)}

			{oembed && showModal && (
				<Modal
					className="modal--video"
					onCancel={() => setShowModal(false)}
					headerClassNames={[]}
					bodyClassNames={[]}
					showCancel={false}
					showSubmit={false}
					trapFocus
				>
					<div
						className="video__embed"
						dangerouslySetInnerHTML={{
							__html: oembed,
						}}
						ref={videoModalRef}
					/>
				</Modal>
			)}

			{!hideVideoButton && (videoWallpaper || !isPlaying) && (
				<button
					className="btn-play"
					onClick={() => {
						if (oembed) {
							setShowModal(true);
							addTabIndex();
						} else {
							setIsPlaying((prev) => !prev);
						}
					}}
				>
					{playLabel}
				</button>
			)}
		</div>
	);
};

export default Video;
